import {
  Calculator,
  ChartLine,
  Inbox,
  Info,
  Newspaper,
  Settings,
  ShieldCheck,
  TrendingUp,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import type { CommonKey } from '@/shared/i18n/common';
import type { DomainIconId } from '../icons/domainIconTypes';

export type NavIconSpec =
  { kind: 'domain'; id: DomainIconId } | { kind: 'lucide'; icon: LucideIcon };
export type NavGroupId = 'mercado' | 'herramientas' | 'informacion' | 'cuenta';
export type NavItemId =
  | 'inicio'
  | 'precios'
  | 'historico'
  | 'comparador'
  | 'tendencias'
  | 'noticias'
  | 'calculadora'
  | 'alertas'
  | 'notificaciones'
  | 'fuentes'
  | 'ajustes'
  | 'acerca'
  | 'cuenta'
  | 'admin';

export interface NavItem {
  id: NavItemId;
  to: string;
  label: CommonKey;
  icon: NavIconSpec;
  group: NavGroupId;
  /** Prefijo de ruta que lo marca como activo (por defecto, `to`). */
  match?: string;
  /** Solo coincide con la ruta exacta (Inicio). */
  exact?: boolean;
}

export interface NavGroup {
  id: NavGroupId;
  label: CommonKey;
  items: NavItem[];
}

const domain = (id: DomainIconId): NavIconSpec => ({ kind: 'domain', id });
const lucide = (icon: LucideIcon): NavIconSpec => ({ kind: 'lucide', icon });

/** Todas las secciones de la app, en el orden de la barra lateral. */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    id: 'inicio',
    to: '/',
    label: 'nav.inicio',
    icon: domain('pizarra'),
    group: 'mercado',
    exact: true,
  },
  {
    id: 'precios',
    to: '/precios',
    label: 'nav.precios',
    icon: domain('billete'),
    group: 'mercado',
  },
  {
    id: 'historico',
    to: '/historico',
    label: 'nav.historico',
    icon: lucide(ChartLine),
    group: 'mercado',
  },
  {
    id: 'comparador',
    to: '/comparador',
    label: 'nav.comparador',
    icon: domain('balanza'),
    group: 'mercado',
  },
  {
    id: 'tendencias',
    to: '/tendencias',
    label: 'nav.tendencias',
    icon: lucide(TrendingUp),
    group: 'mercado',
  },
  {
    id: 'noticias',
    to: '/noticias',
    label: 'nav.noticias',
    icon: lucide(Newspaper),
    group: 'mercado',
  },
  {
    id: 'calculadora',
    to: '/calculadora',
    label: 'nav.calculadora',
    icon: lucide(Calculator),
    group: 'herramientas',
  },
  {
    id: 'alertas',
    to: '/alertas',
    label: 'nav.alertas',
    icon: domain('campana'),
    group: 'herramientas',
  },
  {
    id: 'notificaciones',
    to: '/notificaciones',
    label: 'nav.notificaciones',
    icon: lucide(Inbox),
    group: 'herramientas',
  },
  {
    id: 'fuentes',
    to: '/fuentes',
    label: 'nav.fuentes',
    icon: domain('mercado'),
    group: 'informacion',
  },
  {
    id: 'ajustes',
    to: '/ajustes',
    label: 'nav.ajustes',
    icon: lucide(Settings),
    group: 'informacion',
  },
  { id: 'acerca', to: '/acerca', label: 'nav.acerca', icon: lucide(Info), group: 'informacion' },
  { id: 'cuenta', to: '/cuenta', label: 'nav.cuenta', icon: lucide(UserRound), group: 'cuenta' },
  { id: 'admin', to: '/admin', label: 'nav.admin', icon: lucide(ShieldCheck), group: 'cuenta' },
];

/** Las cuatro secciones fijas de la barra inferior móvil; la quinta es «Más». */
export const BOTTOM_NAV_IDS: readonly NavItemId[] = ['inicio', 'precios', 'calculadora', 'alertas'];

const GROUP_LABELS: Record<NavGroupId, CommonKey> = {
  mercado: 'nav.groupMercado',
  herramientas: 'nav.groupHerramientas',
  informacion: 'nav.groupInformacion',
  cuenta: 'nav.groupCuenta',
};

export interface NavContext {
  /** El backend ofrece cuentas (capacidades.auth.habilitada). Sin ellas no hay «Mi cuenta» ni «Admin». */
  authEnabled: boolean;
  signedIn: boolean;
  isAdmin: boolean;
}

/** Secciones visibles según el modo y la sesión, agrupadas. Sin sesión, «Mi cuenta» pasa a «Entrar». */
export function navGroups({ authEnabled, signedIn, isAdmin }: NavContext): NavGroup[] {
  const items = NAV_ITEMS.flatMap<NavItem>((item) => {
    if (item.id === 'admin') return authEnabled && signedIn && isAdmin ? [item] : [];
    if (item.id !== 'cuenta') return [item];
    if (!authEnabled) return [];
    return [
      signedIn ? item : { ...item, to: '/cuenta/entrar', label: 'nav.entrar', match: '/cuenta' },
    ];
  });
  return (Object.keys(GROUP_LABELS) as NavGroupId[])
    .map((id) => ({
      id,
      label: GROUP_LABELS[id],
      items: items.filter((item) => item.group === id),
    }))
    .filter((group) => group.items.length > 0);
}

/** ¿La ruta actual pertenece a esta sección? Inicio solo en `/`; el resto, por prefijo de segmento. */
export function isNavActive(pathname: string, item: NavItem): boolean {
  if (item.exact) return pathname === item.to;
  const base = item.match ?? item.to;
  return pathname === base || pathname.startsWith(`${base}/`);
}
